import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';

import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import { Portal } from './Portal';

import './styles.css';

function FadeInAnimation({ children, delay = 300, inProp }) {
  return (
    <CSSTransition unMountOnExit in={inProp} timeout={delay} classNames="fadeAnimation" appear>
      {children}
    </CSSTransition>
  );
}

FadeInAnimation.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  delay: PropTypes.number.isRequired,
  inProp: PropTypes.bool,
};

function PortalTooltip({
  trackingEventName,
  placement = 'bottom',
  refElement,
  boundaryRef,
  tooltipBody,
  className,
  isAlwaysOpen = false,
  children,
}) {
  const [isShown, setIsShown] = useState(isAlwaysOpen);

  const debouncedMouseEvent = useDebouncedCallback((toggle) => {
    // @ts-ignore eslint-disable-next-line no-unused-expressions 
    !isAlwaysOpen && setIsShown(toggle);
  }, 100);

  const [popperElement, setPopperElement] = useState(null);
  const [isHidden, setIsHidden] = useState(true);
  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(refElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [200, 20],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: boundaryRef ?? 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false,
        },
      },
    ],
  });

  setTimeout(() => {
    if (forceUpdate) {
      setIsHidden(false);
      forceUpdate();
    }
  }, 1000);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={className}
      data-ocweb-id={trackingEventName}
      onMouseEnter={() => {
        setIsShown(true);
        debouncedMouseEvent(true);
      }}
      onMouseLeave={() => debouncedMouseEvent(false)}
    >
      {children}
      {isShown && (
        <FadeInAnimation delay={700} inProp={isShown}>
          <Portal>
            <div
              className={isHidden ? 'Mobile-hiddenPopper' : 'Mobile-popper'}
              ref={setPopperElement}
              style={popperStyles.popper}
              {...attributes.popper}
            >
              {tooltipBody}
            </div>
          </Portal>
        </FadeInAnimation>
      )}
    </div>
  );
}

PortalTooltip.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  tooltipBody: PropTypes.element,
  refElement: PropTypes.element,
  boundaryRef: PropTypes.element,
  placement: PropTypes.any,
  className: PropTypes.string,
  trackingEventName: PropTypes.string,
  isAlwaysOpen: PropTypes.bool,
};

export default PortalTooltip;
